import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, signal, computed, ContentChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LucideAngularModule, Search, Pen, EllipsisVertical, Inbox } from 'lucide-angular';

import { TableColumn } from './models/table-column.model';
import { TableAction } from './models/table-action.model';
import { TableConfig, DEFAULT_TABLE_CONFIG } from './models/table-config.model';
import { ButtonDisplayComponent, ButtonDisplayItem } from '../button-display/button-display.component';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatChipsModule,
    MatCheckboxModule,
    LucideAngularModule,
    ButtonDisplayComponent
  ],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent<T = any> implements OnInit, OnChanges {

  // Lucide icons
  readonly Search = Search;
  readonly Pen = Pen;
  readonly EllipsisVertical = EllipsisVertical;
  readonly Inbox = Inbox;
  
  // Menu state: track which row's action menu is open (using row index)
  openMenuRowIndex = signal<number | null>(null);
  
  // Menu position for fixed positioning
  menuPosition = { top: 0, left: 0 };
  
  // Menu items for the currently open menu
  menuItems = signal<ButtonDisplayItem[]>([]);
  
  // ========== INPUTS ==========
  
  /** Datos a mostrar en la tabla */
  @Input() data: T[] = [];
  
  /** Definición de columnas */
  @Input() columns: TableColumn<T>[] = [];
  
  /** Acciones disponibles para cada fila */
  @Input() actions: TableAction<T>[] = [];
  
  /** Estado de carga */
  @Input() loading: boolean = false;
  
  /** Descripción de la tabla para accesibilidad (lectores de pantalla) */
  @Input() tableCaption: string = 'Data table';
  
  /** Configuración de la tabla */
  @Input() config: Partial<TableConfig> = {};
  
  /** Función personalizada de búsqueda */
  @Input() searchFunction?: (item: T, searchTerm: string) => boolean;
  
  /** Función personalizada de filtrado */
  @Input() filterFunction?: (item: T) => boolean;
  
  /** Estado de carga de más datos (para infinite scroll) */
  @Input() loadingMore: boolean = false;
  
  /** Indica si hay más datos para cargar */
  @Input() hasMore: boolean = false;
  
  /** Habilitar paginación del servidor en lugar de local */
  @Input() serverSidePagination: boolean = false;
  
  /** Total de elementos en el servidor (para paginación del servidor) */
  @Input() totalServerItems?: number;
  
  /** Cuando true, permite sorting local incluso con serverSidePagination = true */
  @Input() clientSideSorting: boolean = false;
  
  // ========== OUTPUTS ==========
  
  /** Evento cuando cambia la búsqueda */
  @Output() searchChange = new EventEmitter<string>();
  
  /** Evento cuando cambia la página */
  @Output() pageChange = new EventEmitter<PageEvent>();
  
  /** Evento cuando se hace click en una fila */
  @Output() rowClick = new EventEmitter<T>();
  
  /** Evento cuando cambia el total de resultados */
  @Output() resultsChange = new EventEmitter<number>();
  
  /** Evento cuando se necesita cargar más datos (infinite scroll) */
  @Output() loadMore = new EventEmitter<void>();
  
  /** Evento cuando cambia la selección de filas */
  @Output() selectionChange = new EventEmitter<T[]>();
  
  /** Evento cuando se selecciona/deselecciona una fila individual */
  @Output() rowSelectionChange = new EventEmitter<{row: T, selected: boolean}>();
  
  // ========== TEMPLATES PERSONALIZADOS ==========
  
  @ContentChild('customFilters') customFiltersTemplate?: TemplateRef<any>;
  @ContentChild('emptyState') emptyStateTemplate?: TemplateRef<any>;
  
  // ========== STATE ==========
  
  /** Signal interno para los datos */
  readonly dataSignal = signal<T[]>([]);
  
  /** Signal interno para las columnas */
  readonly columnsSignal = signal<TableColumn<T>[]>([]);
  
  /** Término de búsqueda actual */
  searchTerm = signal<string>('');
  
  /** Índice de página actual */
  pageIndex = signal<number>(0);
  
  /** Tamaño de página actual */
  pageSize = signal<number>(10);
  
  /** Columna de ordenamiento actual */
  sortColumn = signal<string>('');
  
  /** Dirección de ordenamiento actual */
  sortDirection = signal<'asc' | 'desc' | ''>('');
  
  /** Configuración completa (default + custom) */
  fullConfig = signal<TableConfig>(DEFAULT_TABLE_CONFIG);
  
  /** Filas seleccionadas */
  selectedRows = signal<T[]>([]);
  
  /** Signal para trackear si todas las filas están seleccionadas */
  allSelected = computed(() => {
    const visibleData = this.paginatedData();
    const selected = this.selectedRows();
    return visibleData.length > 0 && visibleData.every(row => 
      selected.some(selectedRow => this.compareRows(row, selectedRow))
    );
  });
  
  /** Signal para trackear si hay selección parcial */
  partialSelection = computed(() => {
    const visibleData = this.paginatedData();
    const selected = this.selectedRows();
    const hasSelectedInPage = visibleData.some(row => 
      selected.some(selectedRow => this.compareRows(row, selectedRow))
    );
    return hasSelectedInPage && !this.allSelected();
  });
  
  /** Datos filtrados por búsqueda y filtros personalizados */
  filteredData = computed(() => {
    let filtered = this.dataSignal();
    
    // Aplicar función de filtrado personalizada
    if (this.filterFunction) {
      filtered = filtered.filter(item => this.filterFunction!(item));
    }
    
    // Si es paginación del servidor (por input o config), no aplicar búsqueda ni ordenamiento local
    const isServerSide = this.serverSidePagination || this.fullConfig().serverSidePagination;
    if (isServerSide) {
      // Permitir sorting local si clientSideSorting está habilitado
      if (this.clientSideSorting) {
        filtered = this.applySort(filtered);
      }
      return filtered;
    }
    
    // Aplicar búsqueda
    filtered = this.applySearch(filtered);
    
    // Aplicar ordenamiento
    filtered = this.applySort(filtered);
    
    return filtered;
  });
  
  /** Aplicar búsqueda a los datos */
  private applySearch(data: T[]): T[] {
    const search = this.searchTerm().toLowerCase().trim();
    if (!search || !this.fullConfig().searchable) {
      return data;
    }
    
    if (this.searchFunction) {
      return data.filter(item => this.searchFunction!(item, search));
    }
    
    // Búsqueda por defecto en columnas searchable
    return data.filter(item => {
      return this.columns
        .filter(col => col.searchable)
        .some(col => {
          const value = this.getPropertyValue(item, col.key);
          // Use formatter if available, otherwise use raw value
          const searchableValue = col.formatter 
            ? col.formatter(value, item)
            : value;
          return searchableValue?.toString().toLowerCase().includes(search);
        });
    });
  }
  
  /** Aplicar ordenamiento a los datos */
  private applySort(data: T[]): T[] {
    const sortCol = this.sortColumn();
    const sortDir = this.sortDirection();
    
    if (!sortCol || !sortDir) {
      return data;
    }
    
    return [...data].sort((a, b) => {
      const aValue = this.getPropertyValue(a, sortCol);
      const bValue = this.getPropertyValue(b, sortCol);
      
      const comparison = this.compareValues(aValue, bValue);
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }
  
  /** Comparar dos valores para ordenamiento */
  private compareValues(aValue: any, bValue: any): number {
    // Manejar valores nulos/undefined
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    // Comparación según tipo
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return aValue - bValue;
    }
    
    if (aValue instanceof Date && bValue instanceof Date) {
      return aValue.getTime() - bValue.getTime();
    }
    
    return String(aValue).localeCompare(String(bValue));
  }
  
  /** Datos paginados */
  paginatedData = computed(() => {
    const filtered = this.filteredData();
    
    // Si es paginación del servidor (por input o config), mostrar todos los datos que nos enviaron
    const isServerSide = this.serverSidePagination || this.fullConfig().serverSidePagination;
    if (isServerSide) {
      return filtered;
    }
    
    // Si no, aplicar paginación local
    if (!this.fullConfig().pageable) {
      return filtered;
    }
    
    const startIndex = this.pageIndex() * this.pageSize();
    const endIndex = startIndex + this.pageSize();
    
    return filtered.slice(startIndex, endIndex);
  });
  
  /** Columnas visibles */
  visibleColumns = computed(() => {
    return this.columnsSignal().filter(col => col.visible !== false);
  });
  
  /** IDs de columnas para mat-table */
  displayedColumns = computed(() => {
    const cols: string[] = [];
    
    // Agregar columna de selección si está habilitada
    if (this.fullConfig().multiSelect) {
      cols.push('select');
    }
    
    // Agregar columnas visibles
    cols.push(...this.visibleColumns().map(col => col.key));
    
    // Agregar columna de acciones
    if (this.actions.length > 0) {
      cols.push('actions');
    }
    
    return cols;
  });
  
  // ========== LIFECYCLE ==========
  
  ngOnInit(): void {
    this.updateConfig();
    this.dataSignal.set(this.data || []);
    this.columnsSignal.set(this.columns || []);
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.updateConfig();
    }
    
    if (changes['columns']) {
      this.columnsSignal.set(this.columns || []);
    }
    
    if (changes['data']) {
      // Actualizar signal interno cuando cambian los datos
      this.dataSignal.set(this.data || []);
      if (!changes['data'].firstChange) {
        // Reset página solo si no es paginación del servidor
        if (!this.serverSidePagination && !this.fullConfig().serverSidePagination) {
          this.pageIndex.set(0);
        }
        this.clearSelection();
      }
    }
    
    // Emitir cambio de resultados
    if (changes['data']) {
      this.resultsChange.emit(this.getTotalResults());
    }
  }
  
  // ========== METHODS ==========
  
  /** Actualizar configuración combinando default + custom */
  private updateConfig(): void {
    this.fullConfig.set({
      ...DEFAULT_TABLE_CONFIG,
      ...this.config
    });
    
    this.pageSize.set(this.fullConfig().defaultPageSize!);
  }
  
  /** Obtener valor de propiedad anidada (ej: 'user.profile.name') */
  private getPropertyValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  }
  
  /** Manejar cambio en búsqueda */
  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.pageIndex.set(0); // Reset a primera página
    this.searchChange.emit(value);
    this.resultsChange.emit(this.filteredData().length);
  }
  
  /** Manejar cambio de página */
  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.pageChange.emit(event);
  }
  
  /** Manejar cambio de ordenamiento */
  onSortChange(sort: Sort): void {
    this.sortColumn.set(sort.active);
    this.sortDirection.set(sort.direction);
    this.pageIndex.set(0); // Reset a primera página al ordenar
  }
  
  /** Manejar click en fila */
  onRowClick(row: T): void {
    this.rowClick.emit(row);
  }
  
  /** Ejecutar acción */
  executeAction(action: TableAction<T>, row: T, event?: Event): void {
    if (event) {
      event.stopPropagation(); // Prevenir propagación al click de fila
    }
    if (!this.isActionDisabled(action, row)) {
      action.callback(row);
    }
  }
  
  /** Verificar si acción está visible */
  isActionVisible(action: TableAction<T>, row: T): boolean {
    return action.isVisible ? action.isVisible(row) : true;
  }
  
  /** Verificar si acción está deshabilitada */
  isActionDisabled(action: TableAction<T>, row: T): boolean {
    return action.isDisabled ? action.isDisabled(row) : false;
  }
  
  /** Obtener valor formateado de una celda */
  getCellValue(row: T, column: TableColumn<T>): any {
    const value = this.getPropertyValue(row, column.key);
    
    if (column.formatter) {
      return column.formatter(value, row);
    }
    
    return value;
  }
  
  /** Obtener template personalizado por nombre */
  getCustomTemplate(templateName: string): TemplateRef<any> | undefined {
    // Este método se puede extender para buscar templates por nombre
    return undefined;
  }
  
  /** Obtener total de resultados (local o servidor) */
  getTotalResults(): number {
    // Si es paginación del servidor (por input o config), usar el total del servidor
    const isServerSide = this.serverSidePagination || this.fullConfig().serverSidePagination;
    const serverTotal = this.totalServerItems ?? this.fullConfig().totalServerItems;
    
    if (isServerSide && serverTotal !== undefined) {
      return serverTotal;
    }
    
    // Si no, usar total local (filtrado)
    return this.filteredData().length;
  }
  
  /** Obtener texto del contador de resultados */
  getResultsCountText(): string {
    const total = this.getTotalResults();
    const config = this.fullConfig();
    const text = total === 1 ? config.resultsSingular : config.resultsPlural;
    return `${total} ${text}`;
  }
  
  /** Obtener clase CSS para un badge basado en colorMap o booleanos */
  getBadgeClass(row: T, column: TableColumn<T>): string {
    if (!column.badgeConfig) return '';
    const value = column.badgeConfig.getValue(row);
    // Si hay getColor, úsalo
    if (column.badgeConfig.getColor && typeof value === 'string') {
      return column.badgeConfig.getColor(value);
    }
    // Si hay colorMap y el valor es string o number, buscar la clase en el mapa
    if (column.badgeConfig.colorMap && (typeof value === 'string' || typeof value === 'number')) {
      return column.badgeConfig.colorMap[value as string | number] || '';
    }
    // Si es booleano, usar trueClass/falseClass
    if (typeof value === 'boolean') {
      return value ? (column.badgeConfig.trueClass || '') : (column.badgeConfig.falseClass || '');
    }
    return '';
  }

  /** Obtener texto para un badge basado en valor o labels */
  getBadgeText(row: T, column: TableColumn<T>): string {
    if (!column.badgeConfig) return '';
    
    const value = column.badgeConfig.getValue(row);
    
    // Si es booleano, usar trueLabel/falseLabel
    if (typeof value === 'boolean') {
      return value ? (column.badgeConfig.trueLabel || '') : (column.badgeConfig.falseLabel || '');
    }
    
    // Si es string o number, mostrar el valor directamente
    return String(value);
  }

  /** Manejar scroll para infinite scroll */
  onTableScroll(event: Event): void {
    if (!this.fullConfig().infiniteScroll || this.loadingMore || !this.hasMore) {
      return;
    }
    
    const element = event.target as HTMLElement;
    const threshold = this.fullConfig().scrollThreshold || 100;
    
    // Detectar si estamos cerca del final
    if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
      this.loadMore.emit();
    }
  }
  
  // ========== MÉTODOS DE SELECCIÓN ==========
  
  /** Comparar dos filas para determinar igualdad */
  private compareRows(row1: T, row2: T): boolean {
    // Si ambos objetos tienen un id, comparar por id
    if ((row1 as any).id !== undefined && (row2 as any).id !== undefined) {
      return (row1 as any).id === (row2 as any).id;
    }
    
    // Si no, comparar por referencia
    return row1 === row2;
  }
  
  /** Verificar si una fila está seleccionada */
  isRowSelected(row: T): boolean {
    return this.selectedRows().some(selectedRow => this.compareRows(row, selectedRow));
  }
  
  /** Seleccionar/deseleccionar una fila individual */
  toggleRowSelection(row: T, selected?: boolean): void {
    const currentSelected = this.selectedRows();
    const isCurrentlySelected = this.isRowSelected(row);
    const newSelected = selected !== undefined ? selected : !isCurrentlySelected;
    
    let updatedSelected: T[];
    
    if (newSelected && !isCurrentlySelected) {
      // Agregar a la selección
      updatedSelected = [...currentSelected, row];
    } else if (!newSelected && isCurrentlySelected) {
      // Remover de la selección
      updatedSelected = currentSelected.filter(selectedRow => !this.compareRows(row, selectedRow));
    } else {
      // Sin cambios
      return;
    }
    
    this.selectedRows.set(updatedSelected);
    this.rowSelectionChange.emit({ row, selected: newSelected });
    this.selectionChange.emit(updatedSelected);
  }
  
  /** Seleccionar/deseleccionar todas las filas visibles */
  toggleAllSelection(): void {
    const visibleData = this.paginatedData();
    const currentSelected = this.selectedRows();
    const allCurrentlySelected = this.allSelected();
    
    let updatedSelected: T[];
    
    if (allCurrentlySelected) {
      // Deseleccionar todas las filas visibles
      updatedSelected = currentSelected.filter(selectedRow => 
        !visibleData.some(visibleRow => this.compareRows(selectedRow, visibleRow))
      );
    } else {
      // Seleccionar todas las filas visibles
      const newSelections = visibleData.filter(visibleRow => 
        !currentSelected.some(selectedRow => this.compareRows(visibleRow, selectedRow))
      );
      updatedSelected = [...currentSelected, ...newSelections];
    }
    
    this.selectedRows.set(updatedSelected);
    this.selectionChange.emit(updatedSelected);
  }
  
  /** Limpiar toda la selección */
  clearSelection(): void {
    this.selectedRows.set([]);
    this.selectionChange.emit([]);
  }
  
  /** Obtener filas seleccionadas */
  getSelectedRows(): T[] {
    return this.selectedRows();
  }
  
  /** Establecer filas seleccionadas programáticamente */
  setSelectedRows(rows: T[]): void {
    this.selectedRows.set(rows);
    this.selectionChange.emit(rows);
  }

  /** Manejar cambio de checkbox (evita problemas de propagación de eventos) */
  onCheckboxChange(event: any, row: T): void {
    event.stopPropagation();
    this.toggleRowSelection(row, event.checked);
  }
  
  /**
   * Toggle the action menu for a specific row
   */
  toggleActionMenu(event: Event, rowIndex: number, row: T): void {
    event.stopPropagation();
    const currentOpenMenu = this.openMenuRowIndex();
    
    if (currentOpenMenu === rowIndex) {
      // Close if already open
      this.openMenuRowIndex.set(null);
      this.menuItems.set([]);
    } else {
      // Open menu and calculate position
      const buttonElement = event.target as HTMLElement;
      const button = buttonElement.closest('button');
      
      if (button) {
        const rect = button.getBoundingClientRect();
        
        // Position of the menu buttons
        this.menuPosition = {
          top: rect.top + (rect.height / 2) -44, 
          left: rect.left - 4 
        };
      }
      
      // Set menu items for this row
      this.menuItems.set(this.getRowActionItems(row));
      this.openMenuRowIndex.set(rowIndex);
    }
  }
  
  /**
   * Close the action menu
   */
  closeActionMenu(): void {
    this.openMenuRowIndex.set(null);
    this.menuItems.set([]);
  }
  
  /**
   * Check if action menu is open for a specific row
   */
  isActionMenuOpen(rowIndex: number): boolean {
    return this.openMenuRowIndex() === rowIndex;
  }
  
  /**
   * Convert table actions to ButtonDisplayItem[] for app-button-display component
   */
  getRowActionItems(row: T): ButtonDisplayItem[] {
    return this.actions
      .filter(action => this.isActionVisible(action, row))
      .map((action, index) => ({
        id: `action-${index}-${action.icon}`,
        icon: action.lucideIcon,
        ariaLabel: action.tooltip || '',
        action: () => {
          this.executeAction(action, row);
          this.closeActionMenu();
        },
        type: action.cssClass || ''
      }));
  }

  /**
   * Get number of visible actions for a row
   */
  getVisibleActionsCount(row: T): number {
    return this.actions.filter(action => this.isActionVisible(action, row)).length;
  }

  /**
   * Get the first visible action for a row (when there's only one action)
   */
  getFirstVisibleAction(row: T): TableAction<T> | undefined {
    return this.actions.find(action => this.isActionVisible(action, row));
  }

  /**
   * Handle chip click with optional callback and clickability check
   */
  onChipClick(event: Event, column: TableColumn<T>, chipItem: any, row: T): void {
    event.stopPropagation(); // Prevent row click
    
    if (!column.chipsConfig?.onClick) {
      return;
    }
    
    // Check if chip is loading
    if (column.chipsConfig.isLoading && column.chipsConfig.isLoading(chipItem, row)) {
      return; // Don't allow clicks while loading
    }
    
    // Check if chip is clickable
    if (column.chipsConfig.isClickable && !column.chipsConfig.isClickable(chipItem, row)) {
      return;
    }
    
    column.chipsConfig.onClick(chipItem, row);
  }

  /**
   * Check if a chip is clickable
   */
  isChipClickable(column: TableColumn<T>, chipItem: any, row: T): boolean {
    if (!column.chipsConfig?.onClick) {
      return false;
    }
    
    // Not clickable if loading
    if (column.chipsConfig.isLoading && column.chipsConfig.isLoading(chipItem, row)) {
      return false;
    }
    
    if (!column.chipsConfig.isClickable) {
      return true; // If no isClickable function, all chips are clickable
    }
    
    return column.chipsConfig.isClickable(chipItem, row);
  }
}
